import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/src/templates/region-page-layout.tsx";
import Scene from '../controls/scene-control';
import Chapter from '../controls/chapter-control';
import YouTube from '../controls/youtube-control';
import Legend from '../controls/legend-control';
import Expando from '../controls/expando-control';
import LinkToScene from '../controls/link-to-scene-control';
import Carousel from '../controls/carousel-control';
import ExternalLink from '../controls/external-link-control';
import Image from '../controls/image-control';
import Icon from '../controls/icon-control';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Chapter title="" mdxType="Chapter">
  <Scene index={10} mdxType="Scene">
        <h1>{`About the Metropolitan region`}</h1>
        <p>{`The Metropolitan Region includes greater metropolitan Melbourne, extensive grasslands in its west, and large
areas of forest in its east. Our region covers 3.96% of Victoria: 21% of the region is public land and 79% is
privately owned. Over 70% of Victorians live in the Metropolitan Region.`}</p>
        <h1>{`How Victoria manages bushfire risk`}</h1>
        <p>{`In Victoria, we conduct fuel management activities like planned burning, mulching and slashing to
reduce the risk of bushfires causing harm to things we care about. We also conduct planned burning to support plants and animals that need fire for their survival.`}</p>
        <Expando title="Expand to read more about the strategy" mdxType="Expando">
          <p>{`Fuel management planning involves working with local communities to understand what they value.
We also learn from past bushfires and analyse which areas are most at risk of being impacted by bushfire.`}</p>
          <p>{`We use this information to determine where, when and how we undertake fuel management activities.`}</p>
          <p>{`To ensure our fuel management is informed by local contexts, we've developed unique strategies
for different parts of Victoria, guided by Victoria's Emergency Management regions.`}</p>
          <p>{`Within these regions, other relevant boundaries for bushfire management include Country Fire Authority (CFA)
regional and district boundaries, as well as local government areas.`}</p>
          <p>{`Fuel management is just one of many actions that helps reduce bushfire risk.`}</p>
        </Expando>
        <ExternalLink href="https://www.safertogether.vic.gov.au/" mdxType="ExternalLink">
  Click here for more information on other actions that agencies and communities
  can do to reduce bushfire risk
        </ExternalLink>
        <h1>{`On this page`}</h1>
        <LinkToScene index={15} mdxType="LinkToScene">Identifying what we care about</LinkToScene>
        <LinkToScene index={70} mdxType="LinkToScene">Our bushfire history</LinkToScene>
        <LinkToScene index={110} mdxType="LinkToScene">Bushfire risk in our region</LinkToScene>
        <LinkToScene index={140} mdxType="LinkToScene">Our fuel management strategy</LinkToScene>
        <LinkToScene index={190} mdxType="LinkToScene">Bushfire Risk Engagement Areas</LinkToScene>
  </Scene>
 </Chapter>
    <Chapter title="Identifying what we care about" mdxType="Chapter">
  <Scene index={15} mdxType="Scene">
        <p>{`Ultimately, we manage bushfire risk to protect the things we care about. `}</p>
        <p>{`Our bushfire management planning across the state seeks to protect human life, private property, our economy, critical infrastructure, cultural heritage and the environment.`}</p>
        <p>{`Here in the Metropolitan region, we have worked with local communities and consulted scientific data and experts to understand where the most important values and assets are in our region and how to protect them from bushfire.`}</p>
        <p>{`Here's some examples of the different values and assets we considered in our
fuel management planning.`}</p>
      </Scene>
      <Scene index={20} mdxType="Scene">
        <h1><Icon icon="child" mdxType="Icon" />{` Human life`}</h1>
        <p>{`Communities identified human life and property as the most important things we need to protect from bushfire.`}</p>
        <p>{`Over four million Victorians live in Metropolitan region within a diverse array of townships that have different needs for managing bushfire risk. `}</p>
        <p>{`For example, some communities live close to forested areas in and around the Dandenong Ranges, Warrandyte and the Yarra Valley,`}</p>
      </Scene>
      <Scene index={21} mdxType="Scene">
        <p>{`whereas others live in more open areas, such as the western grasslands. We carefully consider the needs of our different communities in our
bushfire management planning.`}</p>
        <Image src="/images/metro/dandenong_s_planned_burn_j_wand.jpg" mdxType="Image">
  A planned burn in the Dandenong Ranges - Jo Wand
        </Image>
      </Scene>
      <Scene index={30} mdxType="Scene">
        <h1><Icon icon="leaf" mdxType="Icon" />{` Natural environment`}</h1>
        <p>{`Protecting the natural environment is important to communities in the region. Public land
provides important habitat for many species, and ensures it is cared for for generations to come. Habitat
also exists on private land.`}</p>
        <Legend colour="rgba(112,168,0,0.7)" mdxType="Legend">Parks and Reserves</Legend>
        <Legend colour="rgba(209,255,115,0.7)" mdxType="Legend">State Forest</Legend>
      </Scene>
      <Scene index={31} mdxType="Scene">
        <p>{`Iconic and threatened/endangered species such as the Helmeted Honeyeater,`}</p>
        <Legend colour="rgba(132,0,168,0.7)" mdxType="Legend">Helmeted Honeyeater Habitat</Legend>
      </Scene>
      <Scene index={32} mdxType="Scene">
        <p>{`Leadbeater's Possum,`}</p>
        <Legend colour="rgba(255,0,0,0.7)" mdxType="Legend">Leadbeaters Possum Habitat</Legend>
      </Scene>
      <Scene index={33} mdxType="Scene">
        <p>{`and Greater Glider call this region home. `}</p>
        <Legend colour="rgba(158,170,215,0.7)" mdxType="Legend">Greater Glider Habitat</Legend>
      </Scene>
      <Scene index={34} mdxType="Scene">
        <p>{`Other non-threatened species such as the Eastern Pygmy Possum also live in our region.`}</p>
        <Legend colour="rgba(215,158,158,0.7)" mdxType="Legend">Eastern Pygmy Possum Habitat</Legend>
      </Scene>
      <Scene index={35} mdxType="Scene">
        <p>{`We carefully consider where, how much and how often we burn to promote the health of plants and animals. Some species are dependent on fire for their survival, while other species can be negatively impacted by fire. `}</p>
        <p>{`For example, we burn regularly in the western grasslands to promote species diversity.`}</p>
        <Legend colour="rgba(230,230,0,0.7)" mdxType="Legend">Grasslands in the West of Melbourne</Legend>
      </Scene>
      <Scene index={36} mdxType="Scene">
        <p>{`and in the wet Mountain Ash forest, we don't burn at all.`}</p>
        <Legend colour="rgba(0,76,115,0.7)" mdxType="Legend">Mountain Ash Forest</Legend>
        <Carousel images={['/images/metro/greaterglider_adamwhitchurch.jpg', '/images/metro/heho_16_9_m_jones.jpg', '/images/metro/wet_mountain_ash_forest.jpg']} credits={['Greater Glider - Adam Whitchurch', 'Helmeted Honeyeater - Michael Jones', 'Mountain Ash - Michael Jones']} mdxType="Carousel" />
        <ExternalLink href="https://www.ffm.vic.gov.au/bushfire-fuel-and-risk-management/plants-and-animals/" mdxType="ExternalLink">
  Click here to learn more about how we consider plants and animals in our
  bushfire management
        </ExternalLink>
      </Scene>
      <Scene index={40} mdxType="Scene">
        <h1><Icon icon="credit-card" mdxType="Icon" />{` Regional economy`}</h1>
        <p>{`Tourism is a major industry in the region. The Mornington Peninsula, Dandenong Ranges and Yarra Valley attract around
15 million domestic and international visitors every year. This contributes around $2 billion to our economy.`}</p>
        <p>{`We also target our fuel management to reduce bushfire risk to these tourism hotspots to maintain a healthy and attractive
environment that can support tourism and local economies.`}</p>
        <Legend colour="rgba(241,171,102,0.7)" mdxType="Legend">Agriculture</Legend>
        <Legend colour="rgba(0,197,255,0.7)" mdxType="Legend">Commercial/Industrial</Legend>
        <Legend colour="rgba(2,177,89,0.7)" mdxType="Legend">Native Vegetation</Legend>
        <Legend colour="rgba(226,51,90,0.7)" mdxType="Legend">Residential</Legend>
        <Image src="/images/metro/mt_little_joe_planned_burn._view_from_warburton_township_yarra_district_delwp.png" mdxType="Image">
  View from Warburton township of planned burn at Mt Little Joe - DELWP
        </Image>
      </Scene>
      <Scene index={50} mdxType="Scene">
        <h1><Icon icon="broadcast-tower" mdxType="Icon" />{` Critical infrastructure`}</h1>
        <p>{`Protecting our critical infrastructure from bushfire risk is essential for maintaining the health and safety of all Victorians.`}</p>
        <p>{`We have seven water storages and catchments in the region that provide fresh water to 4.5 million Victorians.`}</p>
        <p>{`Fuel management in water catchments reduces the risk of bushfire contamination to our water supply.`}</p>
        <Legend colour="rgba(0,255,197,0.7)" mdxType="Legend">Melbourne Water reservoirs</Legend>
        <Legend colour="rgba(0,169,230,1)" mdxType="Legend">Transmission Lines</Legend>
        <Legend colour="rgba(197,0,255,1)" mdxType="Legend">Fire/Radio Towers</Legend>
        <Carousel images={['/images/metro/upper_yarra_res_planned_burn_16_9_2_justin_jemmeson.jpg', '/images/metro/o_shannassy_catchment_1wk_after_bs_v10_jvp.jpg', '/images/metro/cardinia_reservoir_delwp.jpg']} credits={['Upper Yarra Reservoir Planned Burn 2020 - Justin Jemmeson', 'O\'Shannassy Reservoir, fire crew draughting water - John Van De Paverd', 'Cardinia Reservoir - DELWP']} mdxType="Carousel" />
      </Scene>
      <Scene index={60} mdxType="Scene">
        <h1><Icon icon="cultural-heritage" isImage mdxType="Icon" />{` Cultural heritage`}</h1>
        <p>{`Our region includes thousands of Aboriginal cultural heritage sites, such as artefact scatters, object collections, earth features,
scarred trees, shell middens, stone features and ancestral remains.`}</p>
        <p>{`We work closely with Traditional Owner groups to implement protection measures and avoid harm to these important cultural values.`}</p>
        <p>{`We also support Traditional Owner groups to heal and manage Country through cultural burning.`}</p>
        <Legend colour="rgba(204,148,143,0.7)" mdxType="Legend">Bunurong Land Council Aboriginal Corporation</Legend>
        <Legend colour="rgba(220,207,227,0.7)" mdxType="Legend">Wadawurrung Traditional Owners Aboriginal Corporation</Legend>
        <Legend colour="rgba(245,239,154,0.7)" mdxType="Legend">Wurundjeri Woi Wurrung Cultural Heritage Aboriginal Corporation</Legend>
        <Legend colour="rgba(112,153,89,0.7)" mdxType="Legend">Taungurung Cultural Heritage Aboriginal Corporation</Legend>
        <Legend colour="rgba(170,102,205,0.7)" mdxType="Legend">Other Traditional Owners</Legend>
      </Scene>
    </Chapter>
    <Chapter title="Our bushfire history" mdxType="Chapter">
  <Scene index={70} mdxType="Scene">
        <h1>{`Our bushfire history`}</h1>
        <p><em parentName="p">{`This section contains information regarding past bushfires which some readers may find upsetting.
Click to skip ahead to `}</em><LinkToScene index={110} inline mdxType="LinkToScene"><em parentName="p">{`bushfire risk in our region`}</em></LinkToScene><em parentName="p">{` or
`}</em><LinkToScene index={140} inline mdxType="LinkToScene"><em parentName="p">{`our fuel management strategy`}</em></LinkToScene><em parentName="p">{`.`}</em>{` `}</p>
        <p>{`This map shows the bushfire history in the Metropolitan region from 1983-2020.`}</p>
        <p>{`Bushfire history and fire prediction modelling tell us that our most dangerous bushfires are
likely to start in the north, often entering from Hume region.`}</p>
        <p>{`These bushfires follow a pattern of being dominated by hot north-westerly winds, followed by a
cool afternoon wind change from the south west.`}</p>
        <Legend colour="rgba(115,222,255,0.7)" mdxType="Legend">Fires in 1939</Legend>
        <Legend colour="rgba(95,199,237,0.7)" mdxType="Legend">Fires in the 1940s</Legend>
        <Legend colour="rgba(78,176,222,0.7)" mdxType="Legend">Fires in the 1950s</Legend>
        <Legend colour="rgba(62,154,207,0.7)" mdxType="Legend">Fires in the 1960s</Legend>
        <Legend colour="rgba(48,131,191,0.7)" mdxType="Legend">Fires in the 1970s</Legend>
        <Legend colour="rgba(35,110,176,0.7)" mdxType="Legend">Fires in the 1980s</Legend>
        <Legend colour="rgba(24,90,161,0.7)" mdxType="Legend">Fires in the 1990s</Legend>
        <Legend colour="rgba(15,71,145,0.7)" mdxType="Legend">Fires in the 2000s</Legend>
        <Legend colour="rgba(7,54,130,0.7)" mdxType="Legend">Fires in the 2010s</Legend>
        <Legend colour="rgba(0,38,115,0.7)" mdxType="Legend">Fires in 2020</Legend>
      </Scene>
      <Scene index={80} mdxType="Scene">
        <h1>{`Ash Wednesday`}</h1>
        <p>{`The 1983 Ash Wednesday bushfires followed typical fire behaviour patterns. The fire was dominated by strong
north-westerly winds, and rapidly spread north east under the influence of the cold front that brought south-westerly winds.`}</p>
        <p>{`Beaconsfield Upper, Cockatoo and Warburton experienced combined losses of 27 lives and 572 houses.`}</p>
        <Legend colour="rgba(168,0,0,0.7)" mdxType="Legend">Burnt area</Legend>
        <Carousel images={['/images/metro/1983_ashwednesday_warburton_v3_jvp.jpg', '/images/metro/1983_ashwednesday_warburton_v4_jvp.jpg']} credits={['Warburton township, 1983 Ash Wednesday - John Van De Paverd', '1983 Ash Wednesday bushfires - John Van De Paverd']} mdxType="Carousel" />
      </Scene>
      <Scene index={90} mdxType="Scene">
        <h1>{`Black Saturday`}</h1>
        <p>{`The 2009 Black Saturday Bushfires were the most devasting in Victoria's recorded history. These fires impacted many townships
in the Metropolitan region. The fires followed typical fire behaviour patterns for the area. This state-wide fire event caused
the loss of 173 lives and more than 2,000 houses in Victoria.`}</p>
        <Carousel images={['/images/metro/black_saturday_impacts_parksvictoria.jpg', '/images/metro/black_saturday_epicormic_growth.jpg', '/images/metro/kilmore_black_saturday_2009_bushfire_modelled_and_actual_extent_of_bushfire.jpg']} credits={['Black Saturday Bushfire impact- Parks Victoria', 'Black Saturday epicormic growth - DELWP', 'Kilmore Black Saturday 2009 bushfire modelled and actual extent of bushfire - Frazer Wilson']} mdxType="Carousel" />
        <YouTube id="JysHm6SDnS8" mdxType="YouTube">
  Phoenix simulation of the Black Saturday bushfires in the Metropolitan region.
        </YouTube>
      </Scene>
      <Scene index={100} mdxType="Scene">
        <h1>{`Bunyip and Cambarville`}</h1>
        <p>{`The 2019 Bunyip and Cambarville bushfires were caused by a dry band of lightning in late summer and spread rapidly under the
influence of northerly winds. Both bushfires impacted large areas of threatened species habitat, and the Bunyip fire impacted many houses and sheds.`}</p>
      </Scene>
      <Scene index={101} mdxType="Scene">
        <p>{`This fire event started through multiple lightning strikes igniting bushfires in Bunyip State Forest on the 1st of March 2019.`}</p>
      </Scene>
      <Scene index={102} mdxType="Scene">
        <p>{`The following morning, several of the bushfires merged together and progressed further south.`}</p>
      </Scene>
      <Scene index={103} mdxType="Scene">
        <p>{`Later that day, extreme weather conditions resulted in all active bushfires in the area merging into a large fire complex
that rapidly progressed south.`}</p>
      </Scene>
      <Scene index={104} mdxType="Scene">
        <p>{`On March 3rd, the fire expanded on each front and progressed further into private property to the south.`}</p>
      </Scene>
      <Scene index={105} mdxType="Scene">
        <p>{`North-westerly winds then pushed the fire further to the south east.`}</p>
      </Scene>
      <Scene index={106} mdxType="Scene">
        <p>{`On the morning of the 4th of March, the bushfire complex expanded on all fronts and the fire crossed over the
Princess Highway and the transmission lines that provide power to suburban Melbourne`}</p>
      </Scene>
      <Scene index={107} mdxType="Scene">
        <p>{`The fire complex then progressed further north past the transmission lines.`}</p>
      </Scene>
      <Scene index={108} mdxType="Scene">
        <p>{`Firefighters continued to battle the blaze for several days. It was declared 'contained' on March 13, 2020.`}</p>
        <Carousel images={['/images/metro/bunyip_1_month_post_fire_jacque_quaine.jpg', '/images/metro/bunyip_3_months_post_fire_jacque_quaine.jpg', '/images/metro/bunyip_8_months_post_fire_jacque_quaine.jpg']} credits={['Bunyip State Park one month post bushfire - Jacqueline Quaine', 'Bunyip State Park three months post bushfire- Jacqueline Quaine', 'Bunyip State Park eight months post bushfire- Jacqueline Quaine']} mdxType="Carousel" />
        <ExternalLink href="https://www.ffm.vic.gov.au/history-and-incidents/past-bushfires/" mdxType="ExternalLink">
  Click here to learn more about Victoria’s bushfire history
        </ExternalLink>
      </Scene>
    </Chapter>
    <Chapter title="Bushfire risk in our region" mdxType="Chapter">
  <Scene index={110} mdxType="Scene">
        <h1>{`Bushfire risk in our region`}</h1>
        <p>{`This map shows the risk of house loss in the Metropolitan region. It compares where houses could be destroyed
by bushfire across the region.`}</p>
        <p>{`Different shades represent different levels of risk. As the shades progress from yellow through red to purple, more and more
houses are potentially destroyed.`}</p>
        <p>{`The purple areas have the highest risk of house loss. More houses could potentially be destroyed by bushfire in these
areas than in any other areas in this region.`}</p>
        <Legend colour="rgba(132,0,168,0.7)" mdxType="Legend">Highest Risk</Legend>
        <Legend colour="rgba(168,0,0,0.7)" mdxType="Legend">Higher Risk</Legend>
        <Legend colour="rgba(255,0,0,0.7)" mdxType="Legend">High Risk</Legend>
        <Legend colour="rgba(224,132,101,0.7)" mdxType="Legend">Intermediate Risk</Legend>
        <Legend colour="rgba(255,170,0,0.7)" mdxType="Legend">Low-Intermediate Risk</Legend>
        <p>{`Computer simulations are used to help us estimate houses lost from bushfires. The simulations measure how
damaging bushfires could be in different locations. They assume that the fuels in the landscape have increased
to the highest level.`}</p>
        <p>{`This helps us plan where, how often and how much fuel management we do to reduce risks to communities over years,
or even decades.`}</p>
        <YouTube id="R8QihtP-lNQ" mdxType="YouTube">
  Example of bushfire simulation using Phoenix RapidFire software.
        </YouTube>
        <p>{`This map does not reflect any recent bushfires or activities that could change the risk in the region. Importantly,
this map shows where there is potential for a bushfire to destroy more houses compared to other parts of the region.
It does not show risk to individual houses.`}</p>
      </Scene>
      <Scene index={120} mdxType="Scene">
        <h1>{`Higher risk area: Dandenong Ranges`}</h1>
        <p>{`We consider the Dandenong Ranges to be a high bushfire risk area. There are highly populated residential areas among the forest,
few escape routes and densely forested areas to the north and west of the area. Fire history and our fire modelling tells us many
fires can impact the Dandenong Ranges and cause significant house loss.`}</p>
      </Scene>
      <Scene index={130} mdxType="Scene">
        <h1>{`Lower risk area: Western grasslands`}</h1>
        <p>{`In comparison, the Western grasslands within our region are lower risk, as they are dominated by grass not forest. While grassfires
burn quickly, compared to forest fires, prediction modelling and fire history indicates they are less likely to result in house loss.`}</p>
        <ExternalLink href="https://www.safertogether.vic.gov.au/understanding-risk/" mdxType="ExternalLink">
  Click here to learn more about our computer simulations and how we measure
  risk
        </ExternalLink>
        <ExternalLink href="https://www.cfa.vic.gov.au/plan-prepare/am-i-at-risk" mdxType="ExternalLink">
  Click here to learn more about understanding your risk
        </ExternalLink>
      </Scene>
    </Chapter>
    <Chapter title="Our fuel management strategy" mdxType="Chapter">
  <Scene index={140} mdxType="Scene">
        <h1>{`Our fuel management strategy`}</h1>
        <p>{`By understanding what communities care about, where bushfires have happened before and analysing what areas in the Metropolitan region
are most at risk, we can create a strategy. This strategy tells agencies where, how much and how often to conduct fuel management
activities to reduce bushfire risk.`}</p>
      </Scene>
      <Scene index={141} mdxType="Scene">
        <p>{`We classify patches of public land as different Fire Management Zones - Asset Protection Zones,`}</p>
        <Legend colour="rgba(226,51,90,0.7)" mdxType="Legend">Asset Protection Zone</Legend>
  </Scene>
  <Scene index={142} mdxType="Scene">
        <p>{`Bushfire Moderation Zones,`}</p>
        <Legend colour="rgba(241,171,102,0.7)" mdxType="Legend">Bushfire Moderation Zone</Legend>
  </Scene>
  <Scene index={143} mdxType="Scene">
        <p>{`Landscape Management Zones`}</p>
        <Legend colour="rgba(244,233,55,0.7)" mdxType="Legend">Landscape Management Zone</Legend>
  </Scene>
  <Scene index={144} mdxType="Scene">
        <p>{`and Planned Burning Exclusion Zones.`}</p>
        <Legend colour="rgba(2,177,86,0.7)" mdxType="Legend">Planned Burning Exclusion Zone</Legend>
        <p>{`In each of these zones we manage fuels differently in terms of the frequency and intensity of fuel management
activities like planned burning.`}</p>
        <p>{`Where we conduct fuel management each year is determined by a range of factors, including fuel loads and weather conditions, such as rainfall.`}</p>
        <p>{`'Fuel' is any grass, leaf litter, twigs, bark and other live vegetation which can burn. Fuel builds up at
different rates in different areas depending on how fast the vegetation grows compared to how fast it decomposes.`}</p>
        <p>{`The way we conduct fuel management in the different types of Fire Management Zones on public land helps us to reduce the intensity,
severity and spread of bushfires across the landscape. This contributes to reducing the risk of bushfires impacting
things we care about.`}</p>
        <p>{`We monitor the success of our fuel management program and publish the results in our yearly Fuel Management Report.`}</p>
  </Scene>
      <Scene index={150} mdxType="Scene">
        <h1>{`Dandenong Ranges`}</h1>
        <p>{`To protect human life and property, we have created `}<strong parentName="p">{`Asset Protection Zones`}</strong>{` to the north west of townships within
the Dandenong Ranges.`}</p>
        <p>{`We conduct fuel management in these zones at regular intervals to keep fuel loads low. Here, our aim in managing fuels is to ensure the greatest protection to life and property.`}</p>
        <Image src="/images/metro/fuel_reduction_is_asset_protect_zone_within_the_dandenong_ranges_to_reduce_risk_to_the_highest_risk_townships_j_wand.jpg" mdxType="Image">
  Fuel reduction within the Dandenong Ranges to reduce risk to the high risk townships - Jo Wand
        </Image>
      </Scene>
      <Scene index={151} mdxType="Scene">
        <p>{`To assist our agency partner Melbourne Water in the protection of the Silvan Reservoir, we have created `}<strong parentName="p">{`Bushfire Moderation Zones`}</strong>{` to the north west of the catchment area. This also helps protect townships further south east such as Monbulk.`}</p>
        <p>{`We conduct fuel management in these zones to reduce the speed and intensity of bushfires and to complement our fuel management activities in `}<strong parentName="p">{`Asset Protection Zones`}</strong>{`.`}</p>
        <p>{`This work also reduces bushfire impacts to the tourism sector, which depends on a healthy and visually attractive
natural environment.`}</p>
      </Scene>
      <Scene index={160} mdxType="Scene">
        <h1>{`Warrandyte and St. Andrews`}</h1>
        <p>{`In the Warrandyte and St. Andrews area, the majority of land is privately owned.`}</p>
        <p>{`We conduct fuel management activities on public land in this area by treating vegetation in `}<strong parentName="p">{`Asset Protection Zones`}</strong>{` to the east of St. Andrews and north of Warrandyte.`}</p>
        <p>{`By treating these areas regularly, we can greatly reduce fine forest fuels. This helps to reduce the intensity and spread of bushfires through these forest areas and provide greater protection to neighbouring townships.`}</p>
      </Scene>
      <Scene index={161} mdxType="Scene">
        <p>{`We have placed `}<strong parentName="p">{`Bushfire Moderation Zones`}</strong>{` to the north east of Warrandyte and St. Andrews. These zones are treated roughly every 10 years.`}</p>
        <p>{`In these `}<strong parentName="p">{`Bushfire Moderation Zones`}</strong>{`, we seek to complement the `}<strong parentName="p">{`Asset Protection Zones`}</strong>{` and reduce the impact of bushfires on these communities.`}</p>
        <p>{`This helps to ensure continuity of tourism and agricultural production in these areas.`}</p>
      </Scene>
      <Scene index={170} mdxType="Scene">
        <h1>{`Warburton`}</h1>
        <p>{`The Warburton Valley is surrounded by a mixture of wet and damp forest with cool temperate rainforest interspersed throughout the area.`}</p>
        <p>{`Because these forests are damp and have high ecological values, we don't undertake fuel management in these areas.`}</p>
        <p>{`These wet forests, like to the north of Warburton, are zoned as `}<strong parentName="p">{`Planned Burn Exclusion Zones`}</strong>{` to indicate we cannot do planned burning here.`}</p>
        <p>{`However, under extreme weather and drought conditions, these areas can still pose bushfire risk to the townships south of the forest.`}</p>
        <Image src="/images/metro/ada_tree_mountain_ash_m_jones.png" mdxType="Image">
  Ada Tree. Mountain Ash forest cannot be treated by fuel management - Michael Jones
        </Image>
      </Scene>
      <Scene index={171} mdxType="Scene">
        <p>{`We've placed `}<strong parentName="p">{`Landscape Management Zones`}</strong>{` south of the Warburton Valley.`}</p>
        <p>{`The primary goal of these zones is to maintain and promote ecosystem resilience by using fire to promote plant and animal diversity.`}</p>
        <p>{`Burns within these zones are conducted under cool moderate conditions and aim to achieve a burn coverage of 10-50%.`}</p>
        <p>{`Due to the fire history of the area, we burn infrequently in these `}<strong parentName="p">{`Landscape Management Zones`}</strong>{`.`}</p>
      </Scene>
      <Scene index={180} mdxType="Scene">
        <h1>{`Healesville`}</h1>
        <p>{`Healesville has number of risk factors that we have considered in zoning around this area.`}</p>
        <p>{`The majority of bushfire risk to Healesville comes from the north in the Hume region from public land. The area
from Pheasant Creek to Yarra Junction is also historically known to have highly impactful bushfires.`}</p>
        <p>{`Taking these factors into consideration, we have created areas of `}<strong parentName="p">{`Bushfire Moderation Zones`}</strong>{` and
`}<strong parentName="p">{`Asset Protection Zones`}</strong>{` to the north of the Healesville area.`}</p>
        <p>{`By conducting planned burning in these zones, we can reduce the risk of large-scale bushfires coming from the north
in Hume region and impacting Healesville and surrounding areas.`}</p>
        <p>{`We work closely with Hume region and inform their Fire Management Zoning to ensure townships in our region are considered
in strategic planning.`}</p>
        <p>{`We also work closely with the Healesville community in reducing bushfire risk in this area.`}</p>
        <Image src="/images/metro/wallaby_joey_16_9_jacque_quaine.jpg" mdxType="Image">
  Eastern grey kangaroo and joey at Healesville - Jacqueline Quaine
        </Image>
      </Scene>
      <Scene index={181} mdxType="Scene">
        <ExternalLink href="https://www.ffm.vic.gov.au/bushfire-fuel-and-risk-management/planned-burning-in-victoria" mdxType="ExternalLink">
  Click here to learn more about our fuel management program on public land
        </ExternalLink>
        <ExternalLink href="https://www.safertogether.vic.gov.au/__data/assets/pdf_file/0026/493532/DELWP_BushfireManagementStrategies_2020_Metro_PortPhillip_rr.pdf" mdxType="ExternalLink">
  Click here to read our full Bushfire Management Strategy for the Metropolitan Region
        </ExternalLink>
      </Scene>
    </Chapter>
    <Chapter title="Bushfire Risk Engagement Areas" mdxType="Chapter">
  <Scene index={190} mdxType="Scene">
        <h1>{`Bushfire Risk Engagement Areas (BREAs)`}</h1>
        <p>{`To protect the things we care about, agencies and communities need to consider ways of reducing bushfire
risk across both public and private land.`}</p>
        <p>{`On public land, our fuel management activities are undertaken across different Fire Management Zones.`}</p>
        <p>{`To support bushfire risk reduction on private land, we've identified `}<strong parentName="p">{`Bushfire Risk Engagement Areas`}</strong>{`.`}</p>
        <Legend colour="rgba(223,115,225,0.7)" mdxType="Legend">Bushfire Risk Engagement Areas</Legend>
        <p>{`Bushfire Risk Engagement Areas in the Metropolitan region cover both public and private land and help fire agencies
to prioritise engagement with the community about managing fuels where it can be most effective. This includes
considering a range of actions to reduce bushfire risk.`}</p>
        <p>{`Where and how we engage with the community will also be informed by current conditions, such as fuel loads and rainfall.`}</p>
        <p>{`Managing fuels on private and public land begins with a conversation about the benefits and limitations of fuel
reduction to meet the needs of our local community and environment.`}</p>
        <p>{`There is no new legal obligation for landowners within Bushfire Risk Engagement Areas to reduce bushfire fuels on
their property.`}</p>
        <p>{`By working together, land and agencies and communities will be able to better protect the things we value from being
impacted by bushfire.`}</p>
        <p>{`For more information or to get involved in fuel management activities on private land, `}<a href="https://www.cfa.vic.gov.au/contact/" target="_blank" rel="noopener noreferrer">{`contact your local CFA District`}</a>{`.`}</p>
      </Scene>
      <Scene index={191} mdxType="Scene">
        <h1>{`Monbulk`}</h1>
        <p>{`Monbulk is a high bushfire risk community situated in the Dandenong Ranges. A passionate community group began work
with FFMVic, CFA, Emergency Management Victoria (EMV) and local Council to develop a plan for treating their
bushfire risk. `}</p>
      </Scene>
      <Scene index={192} mdxType="Scene">
        <p>{`The Monbulk community decided to define fuel management priorities for private land in their community. They based
their system on the public land Fire Management Zones (APZ, BMZ, LMZ and PBEZ) and considered local values,
environment and views when placing zones. The zoning system provides guidance to local landowners to manage fuel
loads on their land, to reduce the bushfire risk to themselves and the whole community. `}</p>
        <p>{`One of the many outcomes of this work is that the community and fire agencies have a shared understanding and
responsibility for managing bushfire risk and protecting the local community.  `}</p>
        <Legend colour="rgba(226,51,90,0.7)" mdxType="Legend">Asset Protection Zone</Legend>
        <Legend colour="rgba(241,171,102,0.7)" mdxType="Legend">Bushfire Moderation Zone</Legend>
        <Legend colour="rgba(244,233,55,0.7)" mdxType="Legend">Landscape Management Zone</Legend>
        <Legend colour="rgba(2,177,86,0.7)" mdxType="Legend">Planned Burning Exclusion Zone</Legend>
      </Scene>
      <Scene index={193} mdxType="Scene">
        <h1>{`Cockatoo`}</h1>
        <p>{`In April 2018 FFMVic and CFA successfully completed a cross tenure fuel reduction burn in Cockatoo on public land
at Wights Forest and on the adjacent council-owned Garden City Estate.  `}</p>
        <Legend colour="rgba(0,38,155,0.7)" mdxType="Legend">Extent of planned burn</Legend>
        <p>{`This burn had several objectives - to provide the highest level of bushfire protection to the residents of Cockatoo,
their homes and the community’s assets; and to support research by the Arthur Rylah Institute (ARI) regarding the
optimal fire severity, for germination of Hairpin Banksia seeds and the survival of seedlings across both public
and council land. `}</p>
        <p>{`Many months of planning and community engagement was undertaken prior to the burn by FFMVic, CFA, Cardinia Shire
Council and ARI institute. This collaboration demonstrated the benefits of multiple agencies collaborating on fuel
management across land tenures to reduce bushfire risk, and to inform future planned burning to support ecological
objectives.`}</p>
        <Carousel images={['/images/metro/cockatoo-burn-engagement.jpg', '/images/metro/cockatoo-xtenure-burn-banksia-burning.png']} credits={['Multi-agency community engagement in Cockatoo - DELWP', 'Hairpin Banksia burning during the planned burn in Cockatoo - DELWP']} mdxType="Carousel" />
        <ExternalLink href="https://www.safertogether.vic.gov.au/strategic-bushfire-management-planning" mdxType="ExternalLink">
  Click here to learn more about Bushfire Risk Engagement Areas
        </ExternalLink>
  </Scene>
    </Chapter>
    <Chapter title="Thank you" mdxType="Chapter">
      <Scene index={200} mdxType="Scene">
        <p>{`This concludes our Metropolitan region strategy. You can find out more information
by downloading the full Bushfire Management Strategy. `}</p>
        <p>{`Return to the state view to explore other regional strategies.`}</p>
        <ExternalLink href="https://www.safertogether.vic.gov.au/__data/assets/pdf_file/0026/493532/DELWP_BushfireManagementStrategies_2020_Metro_PortPhillip_rr.pdf" mdxType="ExternalLink">
  Click here to read our full Bushfire Management Strategy for the Metropolitan Region
        </ExternalLink>
        <ExternalLink href="https://www.safertogether.vic.gov.au/strategic-bushfire-management-planning" mdxType="ExternalLink">
  Click here to learn more about strategic bushfire management planning, and to download the strategy in accessible format
        </ExternalLink>
      </Scene>
    </Chapter>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      